/**
 * ドメイン横断一覧 `/domains`。
 *
 * 運営が知りたいのは「DNS が通っているか」「証明書が切れないか」の 2 点なので、
 * その 2 列を名前の直後に置く。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  PageHeader,
  StatusBadge,
  cn,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { CERT_FILTER, DOMAIN_STATUS, labelOf, type DnsCheckResult, type DnsRecordKind } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { daysUntil } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { ConfirmTextDialog } from '../components/dialogs.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { DomainSummary } from '../types/api.js';

const routeApi = getRouteApi(RID.domains);

/**
 * 証明書フィルタの表示名（`CERT_FILTER`）。
 *
 * `labelOf()` を使わないのは、ここが**状態ではなく絞り込み条件**だから。
 * shared の語彙は `expiring` を「期限が近い」と訳すが、
 * フィルタとしては「何日以内なのか」が分からないと選べない
 * （API の判定は 30 日）。未知の値は生値のまま出す。
 */
const CERT_FILTER_LABEL: Readonly<Record<string, string>> = {
  active: '有効',
  expiring: '30 日以内に期限切れ',
  expired: '期限切れ',
  none: '未発行',
};

function certFilterLabel(value: string): string {
  return CERT_FILTER_LABEL[value] ?? value;
}

/** 一覧のチップに出す 4 種。残り（autoconfig 等）は詳細の DNS タブで見る。 */
const CHIP_KINDS: DnsRecordKind[] = ['mx', 'spf', 'dkim', 'dmarc'];

export function DomainsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const [deleteTarget, setDeleteTarget] = React.useState<DomainSummary | null>(null);

  const list = useCursorList<DomainSummary>({
    path: '/domains',
    keyFor: (params) => qk.domains(params),
    filters: {
      q: search.q,
      status: search.status,
      organizationId: search.organizationId,
      nodeId: search.nodeId,
      cert: search.cert,
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const columns: Array<DataTableColumn<DomainSummary>> = [
    {
      id: 'name',
      header: 'ドメイン',
      cell: (d) => (
        <Link
          to="/domains/$domainId"
          params={{ domainId: d.id }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {d.name}
        </Link>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (d) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: d.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {d.organizationName}
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (d) => <StatusBadge status={d.status} size="sm" /> },
    { id: 'dns', header: 'DNS', width: 'w-40', cell: (d) => <DnsChips checks={d.dnsChecks} /> },
    { id: 'cert', header: '証明書', hideBelow: 'md', cell: (d) => <CertCell domain={d} /> },
    { id: 'node', header: '収容ノード', hideBelow: 'lg', cell: (d) => d.nodeHostname ?? '—' },
    { id: 'mailboxes', header: 'メールボックス', align: 'right', hideBelow: 'lg', cell: (d) => formatCount(d.mailboxCount) },
    { id: 'created', header: '追加日', hideBelow: 'lg', cell: (d) => formatDateTime(d.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (d) => <DomainRowActions domain={d} canWrite={canWrite} onDelete={setDeleteTarget} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="ドメイン"
        description="全契約組織のドメインを横断して表示します。"
        actions={<RefetchingDot active={list.query.isFetching && !list.query.isLoading} />}
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="ドメイン名で絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: {} })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(DOMAIN_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
          {
            key: 'cert',
            label: '証明書',
            value: search.cert,
            /*
             * **選択肢は `@mail-sv/shared` の `CERT_FILTER` から作る。**
             * 以前は `expired` / `missing` を送っていて 400 になり（A316）、
             * それを直したときに今度は**画面から `expired` が消えていた**
             * （API は受けるのに「期限切れ」だけ選べない）。
             * 列挙を書き写す限り必ずどちらかにずれるので、出典を 1 つにする。
             */
            options: optionsFrom(CERT_FILTER, certFilterLabel),
            onChange: (v) => setSearch({ cert: v as typeof search.cert }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(d) => d.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="ドメイン"
            onClearFilter={() => void navigate({ search: {} })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <DeleteDomainDialog domain={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  );
}

/* ------------------------------------------------------------- DNS チップ */

const CHIP_TONE: Record<DnsCheckResult, string> = {
  ok: 'bg-success/15 text-success border-success/30',
  mismatch: 'bg-warning/15 text-warning border-warning/30',
  missing: 'bg-destructive/15 text-destructive border-destructive/30',
  error: 'bg-destructive/15 text-destructive border-destructive/30',
  unchecked: 'bg-muted text-muted-foreground border-border',
};

/**
 * 4 種の判定を極小チップで並べる。
 * **色だけで状態を伝えない**ため、各チップに `title` と `aria-label` を付ける（§11.3）。
 */
export function DnsChips({
  checks,
}: {
  checks: Array<{ kind: DnsRecordKind; result: DnsCheckResult }>;
}): React.ReactElement {
  const byKind = new Map(checks.map((c) => [c.kind, c.result]));
  return (
    <div className="flex gap-1">
      {CHIP_KINDS.map((kind) => {
        const result = byKind.get(kind) ?? 'unchecked';
        const text = `${labelOf(kind)}: ${labelOf(result)}`;
        return (
          <span
            key={kind}
            title={text}
            aria-label={text}
            className={cn(
              'inline-flex h-5 items-center rounded border px-1.5 text-[10px] font-medium uppercase',
              CHIP_TONE[result],
            )}
          >
            {kind === 'mx' ? 'MX' : kind.toUpperCase()}
          </span>
        );
      })}
    </div>
  );
}

function CertCell({ domain }: { domain: DomainSummary }): React.ReactElement {
  const days = daysUntil(domain.certificateExpiresAt);
  if (!domain.certificateStatus) return <span className="text-xs text-muted-foreground">未発行</span>;
  return (
    <div className="flex items-center gap-1.5">
      <StatusBadge status={domain.certificateStatus} size="sm" />
      {days !== null ? (
        <span
          className={cn(
            'text-xs tabular',
            days <= 7 ? 'text-destructive' : days <= 30 ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          残 {days} 日
        </span>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- 行アクション */

function DomainRowActions({
  domain,
  canWrite,
  onDelete,
}: {
  domain: DomainSummary;
  canWrite: boolean;
  onDelete: (d: DomainSummary) => void;
}): React.ReactElement {
  const qc = useQueryClient();

  const recheck = useMutation({
    mutationFn: () => api.post(`/domains/${domain.id}/verify`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domains() }),
  });
  const rotateDkim = useMutation({
    mutationFn: () => api.post(`/dkim/keys/rotate`, { domainId: domain.id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domains() }),
  });
  const renewCert = useMutation({
    mutationFn: () => api.post(`/certificates/renew`, { domainId: domain.id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domains() }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${domain.name} の操作`}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{domain.name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/domains/$domainId" params={{ domainId: domain.id }} search={{ tab: 'overview' }}>
            詳細を開く
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => recheck.mutate()} disabled={recheck.isPending}>
          <RefreshCw className="size-4" aria-hidden />
          DNS を今すぐ再チェック
        </DropdownMenuItem>
        {canWrite ? (
          <>
            <DropdownMenuItem onSelect={() => rotateDkim.mutate()} disabled={rotateDkim.isPending}>
              DKIM 鍵をローテート
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => renewCert.mutate()} disabled={renewCert.isPending}>
              証明書を再発行
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onSelect={() => onDelete(domain)}>
              ドメインを削除
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DeleteDomainDialog({
  domain,
  onClose,
}: {
  domain: DomainSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.del(`/domains/${domain?.id ?? ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.domains() });
      onClose();
    },
  });

  if (!domain) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="ドメインを削除"
      description="このドメインのメールボックスはすべて削除予定になり、purge 期限まで実体だけが保持されます。取り消せません。"
      confirmText={domain.name}
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onConfirm={() => mut.mutate()}
    >
      <Badge variant="warning">メールボックス {formatCount(domain.mailboxCount)} 件が影響を受けます</Badge>
    </ConfirmTextDialog>
  );
}
